import { extractIdFromIri } from "../../../utils/extractFromIri";

export const parseList = (txt) =>
  (txt || "")
    .split(/[\n,]+/g)
    .map((s) => s.trim())
    .filter(Boolean);

export const joinList = (arr) => (Array.isArray(arr) ? arr.join(", ") : "");

const pickDatasetObject = (res) => res?.item ?? res?.data?.item ?? res;

const toArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  return value ? [value] : [];
};

export const normalizeVocabularyValues = ({
  rawValue,
  vocabulary = [],
  multiple = false,
}) => {
  const normalizeText = (value) => String(value || "").trim().toLowerCase();

  const options = (Array.isArray(vocabulary) ? vocabulary : [])
    .map((item) => ({
      iri: item?.concept_iri,
      iriKey: normalizeText(item?.concept_iri),
      shortKey: normalizeText(extractIdFromIri(item?.concept_iri)),
      labelKey: normalizeText(item?.label),
    }))
    .filter((item) => item.iri);

  const normalizeSingle = (value) => {
    if (value == null) return "";

    if (typeof value === "object") {
      const nested =
        value?.concept_iri ??
        value?.id ??
        value?.value ??
        value?.code ??
        value?.label;
      return normalizeSingle(nested);
    }

    const text = String(value).trim();
    if (!text) return "";

    const textKey = normalizeText(text);
    const shortKey = normalizeText(extractIdFromIri(text));

    const matched = options.find(
      (opt) =>
        opt.iriKey === textKey ||
        opt.shortKey === textKey ||
        opt.shortKey === shortKey ||
        opt.labelKey === textKey,
    );

    return matched?.iri || text;
  };

  if (multiple) {
    return toArray(rawValue).map(normalizeSingle).filter(Boolean);
  }

  if (Array.isArray(rawValue)) {
    return normalizeSingle(rawValue[0]);
  }

  return normalizeSingle(rawValue);
};

export function parseDatasetDetailResponse(rawDetail) {
  if (!rawDetail) return null;

  let unwrapped = rawDetail?.data ?? rawDetail;

  if (typeof unwrapped?.body === "string") {
    try {
      unwrapped = JSON.parse(unwrapped.body);
    } catch (e) {
      console.error("Failed to parse dataset body:", e, unwrapped.body);
      return null;
    }
  }

  return pickDatasetObject(unwrapped);
}

export function getParticipantIdFromToken(decoded) {
  const participantId =
    decoded?.participant_id ??
    decoded?.attributes?.participant_id?.[0] ??
    decoded?.attributes?.participant_id ??
    "";

  return Array.isArray(participantId)
    ? (participantId[0] ?? "")
    : participantId;
}

export function buildCreatorOptions({
  participant,
  agents = [],
  lang = "en",
  currentValue = "",
}) {
  const getLocalizedValue = (entries) => {
    if (!Array.isArray(entries) || entries.length === 0) return "";

    const exactMatch = entries.find((entry) => entry?.language === lang);
    if (exactMatch?.value) return exactMatch.value;

    return entries.find((entry) => String(entry?.value || "").trim())?.value || "";
  };

  const options = [];
  const seen = new Set();

  const pushOption = (value, label) => {
    const normalizedValue = String(value || "").trim();
    if (!normalizedValue || seen.has(normalizedValue)) return;

    seen.add(normalizedValue);
    options.push({
      value: normalizedValue,
      label: String(label || "").trim() || extractIdFromIri(normalizedValue),
    });
  };

  pushOption(
    participant?.id,
    getLocalizedValue(participant?.preferredLabel) ||
      getLocalizedValue(participant?.identifier?.[0]?.schemeName) ||
      participant?.identifier?.[0]?.notation,
  );

  (Array.isArray(agents) ? agents : []).forEach((agent) => {
    pushOption(agent?.id, agent?.name);
  });

  pushOption(currentValue, currentValue);

  return options;
}
